import {useCallback, useEffect, useRef, useState} from 'react';

import {getUserPrimaryAccount} from 'src/openapi/admin/apis/AccountOperationsApi';
import {useAppDispatch} from 'src/store/hooks';

type UseAccountNameReturn = {
    accountName: string | null,
    accountId: number | null,
    loading: boolean,
};

export function useUserPrimaryAccountName(): UseAccountNameReturn {
    const dispatch = useAppDispatch();
    const [accountName, setAccountName] = useState<string | null>(null);
    const [accountId, setAccountId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const fetchAccountName = useCallback(async () => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setLoading(true);

        try {
            const response = await dispatch(
                getUserPrimaryAccount(
                    {signal: controller.signal},
                ),
            );

            if (controller.signal.aborted) {
                return;
            }

            if (response.payload?.success) {
                setAccountName(response.payload.data.name);
                setAccountId(response.payload.data.accountId);
            }
        } catch (err) {
            if ((err as {name?: string})?.name === 'AbortError') {
                return;
            }
        } finally {
            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null;
            }
            setLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        fetchAccountName();
        return () => {
            abortControllerRef.current?.abort();
        };
    }, [fetchAccountName]);

    return {accountName, accountId, loading};
}
